import React, { useEffect, useRef, useState, ReactNode } from "react";
import { AssembleeHemicycleIcon } from "@logora/parliament.icons.assemblee_hemicycle_icon";
import { AssembleeHemicycleGraphIcon } from "@logora/parliament.icons.assemblee_hemicycle_graph_icon";
import styles from "./AssembleeHemicycle.module.scss";

interface Seat {
  id: string | number;
  color: string;
  label?: ReactNode;
  hoverable?: boolean;
  focusable?: boolean;
  labelBackgroundColor?: string | undefined;
}

interface LegendItem {
  label: string;
  color: string;
}

interface AssembleeHemicycleProps {
  /** Svg type */
  type?: "default" | "graph";
  /** Highlighted seats objects */
  highlightedSeats: Seat[];
  /** Legend */
  legend?: LegendItem[];
}

interface Label {
  content: ReactNode;
  color: string;
  x: number;
  y: number;
  labelBackgroundColor: string | undefined;
}

export const AssembleeHemicycle = ({
  type = "default",
  highlightedSeats,
  legend,
}: AssembleeHemicycleProps) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  const [hoveredLabel, setHoveredLabel] = useState<{
    content: ReactNode;
    color: string;
    x: number;
    y: number;
    labelBackgroundColor: string | undefined;
  } | null>(null);

  const [focusedLabel, setFocusedLabel] = useState<Label | null>(null);
  const [staticLabel, setStaticLabel] = useState<Label | null>(null);

  useEffect(() => {
    const seatElements: SVGElement[] = [];

    if (highlightedSeats?.length > 0 && svgRef?.current) {
      const svgElement = svgRef.current;

      highlightedSeats.forEach(
        ({
          id,
          color,
          label,
          hoverable = true,
          focusable = true,
          labelBackgroundColor,
        }: Seat) => {
          if (typeof id !== "string") {
            id = String(id);
          }
          const seatElement =
            svgElement.querySelector<SVGElement>(`#p${id}`) ||
            svgElement.querySelector<SVGElement>(`#${id.replace(/_/g, "\\_")}`);
          if (seatElement) {
            seatElements.push(seatElement);
            (seatElement as SVGElement).style.fill = color;
            const content = label;
            if (content) {
              const rect = seatElement.getBoundingClientRect();
              const svgRect = svgElement.getBoundingClientRect();
              const x = rect.x - svgRect.x + rect.width / 2;
              const y = rect.y - svgRect.y;

              if (hoverable) {
                seatElement.addEventListener("mouseenter", () => {
                  setHoveredLabel({
                    content,
                    color,
                    x,
                    y,
                    labelBackgroundColor,
                  });
                });
                seatElement.addEventListener("mouseleave", () => {
                  setHoveredLabel(null);
                });
              }

              if (focusable) {
                seatElement.style.cursor = "pointer";
                seatElement.addEventListener("click", () => {
                  setFocusedLabel({
                    content,
                    color,
                    x,
                    y,
                    labelBackgroundColor,
                  });
                });
              }

              if (!focusable && !hoverable) {
                setStaticLabel({
                  content,
                  color,
                  x,
                  y,
                  labelBackgroundColor,
                });
              }
            }
          }
        }
      );
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (!seatElements.includes(e.target as SVGElement)) {
        setFocusedLabel(null);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [highlightedSeats]);

  return (
    <>
      <div className={styles.svgContainer}>
        {type === "default" && (
          <AssembleeHemicycleIcon width={500} ref={svgRef} />
        )}
        {type === "graph" && (
          <AssembleeHemicycleGraphIcon width={500} ref={svgRef} />
        )}

        {hoveredLabel && <LabelPopover label={hoveredLabel} zIndex={100} />}

        {focusedLabel && <LabelPopover label={focusedLabel} zIndex={10} />}

        {staticLabel && <LabelPopover label={staticLabel} zIndex={1} />}
      </div>

      {legend && legend?.length > 0 && (
        <div className={styles.legendContainer}>
          {legend.map((item) => (
            <p className={styles.legendItem}>
              <span style={{ backgroundColor: item.color }}></span>
              {item.label}
            </p>
          ))}
        </div>
      )}
    </>
  );
};

function LabelPopover({
  label,
  zIndex = 10,
}: {
  label: Label;
  zIndex?: number;
}) {
  return (
    <div
      style={{
        position: "absolute",
        left: label.x,
        top: label.y - 20,
        transform: "translate(-50%, -100%)",
        backgroundColor: label.labelBackgroundColor || label.color,
        color: "white",
        borderRadius: "4px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        backdropFilter: "blur(4px)",
        zIndex,
      }}
    >
      {label.content}
    </div>
  );
}
